import { useEffect } from "react";
import { m, AnimatePresence } from "framer-motion";
import { useDrag } from "@use-gesture/react";
import { verticalSwipes } from "../lib/telegram";

/**
 * The app's one secondary-surface primitive (frontend.md: "bottom sheets, not
 * centred modals"). Spring mount/unmount, drag-to-dismiss, and — critically —
 * it locks Telegram's vertical swipe while open so a downward drag dismisses the
 * SHEET, not the whole TMA.
 */
export function BottomSheet({
  open,
  onClose,
  children,
  title,
}: {
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  title?: string;
}) {
  useEffect(() => {
    if (open) verticalSwipes.lock();
    return () => verticalSwipes.unlock();
  }, [open]);

  const bind = useDrag(({ last, movement: [, my], velocity: [, vy] }) => {
    if (last && (my > 100 || vy > 0.5)) onClose();
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          <m.div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          />
          <m.div
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-tg-bg-secondary"
            style={{ overscrollBehavior: "none", paddingBottom: "max(16px, var(--safe-bottom))" }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            {/* Drag handle — the grab affordance also carries the gesture. */}
            <div {...bind()} className="flex flex-col items-center pt-3 pb-2 touch-none cursor-grab">
              <div className="h-1 w-10 rounded-full bg-tg-hint/40" />
              {title && <h2 className="mt-3 text-base font-semibold text-tg-text">{title}</h2>}
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-4 pb-2 scroll-area">{children}</div>
          </m.div>
        </>
      )}
    </AnimatePresence>
  );
}
